import { useCallback, useMemo } from 'react';
import { clubApi } from '@repo/api-client';
import { useRevalidator } from 'react-router';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@repo/ui/components/dialog';
import { Button } from '@repo/ui/components/button';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { AlertCircle } from 'lucide-react';
import { DataTable } from '@repo/ui/components/data-table';

const CloseInscriptions = ({ tournament }: { tournament: Tournament }) => {
  const revalidator = useRevalidator();

  const handleChangeStatus = async (status: Tournament['status']) => {
    if (!tournament) return;
    await clubApi.updateTournamentById(tournament.club.id, tournament.id, { status });
    revalidator.revalidate();
  };

  const categorySummary = useCallback(
    (categoryId: string) => {
      const registrations = tournament?.registrations.filter((r) => r.category.id === categoryId);
      if (!registrations) return 'No hay inscripciones, no participará en el torneo';
      if (registrations.length === 0) return 'No hay inscripciones, no participará en el torneo';
      if (registrations.length === 1)
        return 'Solo hay una inscripción, no participará en el torneo';
      if (registrations.length === 2) return 'Solo hay dos inscripciones, solo tendrá un partido';
      if (registrations.length === 3)
        return 'Solo hay tres inscripciones, solo tendrá un grupo y una final';
      return `Ok`;
    },
    [tournament]
  );

  const columns = [
    {
      header: 'Categoría',
      accessorKey: 'name',
    },
    {
      header: 'Resumen',
      accessorKey: 'summary',
      cell: ({ row }: { row: { original: Category } }) => {
        return categorySummary(row.original.id);
      },
    },
    {
      header: 'Cantidad de inscripciones',
      accessorKey: 'registrationsCount',
      cell: ({ row }: { row: { original: Category } }) => {
        return tournament?.registrations.filter((r) => r.category.id === row.original.id).length;
      },
    },
  ];

  const amountOkCategories = useMemo(() => {
    if (!tournament) return 0;
    const categoriesOk = tournament?.categories.filter((category) => {
      const registrations = tournament?.registrations.filter((r) => r.category.id === category.id);
      if (!registrations) return false;
      return registrations.length >= 2;
    });
    return categoriesOk?.length ?? 0;
  }, [tournament]);

  return (
    <Dialog>
      <DialogTrigger>
        <Button>Cerrar inscripciones</Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl! w-full">
        <DialogTitle>Cerrar inscripciones</DialogTitle>
        <DataTable
          total={tournament?.categories.length ?? 0}
          columns={columns}
          data={tournament?.categories ?? []}
        />
        <div className="flex flex-col gap-2 justify-end">
          {amountOkCategories <= 0 && (
            <Alert>
              <AlertCircle className="w-4 h-4" />
              <AlertTitle>No hay categorías con suficientes inscripciones</AlertTitle>
              <AlertDescription>
                Para cerrar las inscripciones, debe haber al menos una categoría con suficientes
                inscripciones.
              </AlertDescription>
            </Alert>
          )}
          <Button disabled={amountOkCategories <= 0} onClick={() => handleChangeStatus('ACTIVE')}>
            Cerrar inscripciones
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CloseInscriptions;
